/**
 * Country reference data for the Website Settings → Country access card.
 *
 * The owner-hub country filter stores ISO 3166-1 **alpha-2** codes (`RO`, `MX`), while the world-map shapes the card
 * paints (`components/modules/countries/assets/countries`, reused by the customizer's `WorldMap`) are keyed by
 * **alpha-3** (`ROU`, `MEX`). {@link ALPHA3_BY_ALPHA2} bridges the two; its keys are also the list of countries the card
 * offers (every officially assigned ISO 3166-1 code, plus `XK` Kosovo, which IP geolocation reports).
 *
 * Country names are NOT in the translation catalogs: {@link countryName} resolves them with `Intl.DisplayNames` in the
 * app's language (the caller passes its base language, `adLanguageBase(i18n.language)`), so 250 names never become
 * i18n keys and the list reads in the UI's language.
 */

export const ALPHA3_BY_ALPHA2: Readonly<Record<string, string>> = {
  AD: 'AND', AE: 'ARE', AF: 'AFG', AG: 'ATG', AI: 'AIA', AL: 'ALB', AM: 'ARM', AO: 'AGO', AQ: 'ATA', AR: 'ARG',
  AS: 'ASM', AT: 'AUT', AU: 'AUS', AW: 'ABW', AX: 'ALA', AZ: 'AZE', BA: 'BIH', BB: 'BRB', BD: 'BGD', BE: 'BEL',
  BF: 'BFA', BG: 'BGR', BH: 'BHR', BI: 'BDI', BJ: 'BEN', BL: 'BLM', BM: 'BMU', BN: 'BRN', BO: 'BOL', BQ: 'BES',
  BR: 'BRA', BS: 'BHS', BT: 'BTN', BV: 'BVT', BW: 'BWA', BY: 'BLR', BZ: 'BLZ', CA: 'CAN', CC: 'CCK', CD: 'COD',
  CF: 'CAF', CG: 'COG', CH: 'CHE', CI: 'CIV', CK: 'COK', CL: 'CHL', CM: 'CMR', CN: 'CHN', CO: 'COL', CR: 'CRI',
  CU: 'CUB', CV: 'CPV', CW: 'CUW', CX: 'CXR', CY: 'CYP', CZ: 'CZE', DE: 'DEU', DJ: 'DJI', DK: 'DNK', DM: 'DMA',
  DO: 'DOM', DZ: 'DZA', EC: 'ECU', EE: 'EST', EG: 'EGY', EH: 'ESH', ER: 'ERI', ES: 'ESP', ET: 'ETH', FI: 'FIN',
  FJ: 'FJI', FK: 'FLK', FM: 'FSM', FO: 'FRO', FR: 'FRA', GA: 'GAB', GB: 'GBR', GD: 'GRD', GE: 'GEO', GF: 'GUF',
  GG: 'GGY', GH: 'GHA', GI: 'GIB', GL: 'GRL', GM: 'GMB', GN: 'GIN', GP: 'GLP', GQ: 'GNQ', GR: 'GRC', GS: 'SGS',
  GT: 'GTM', GU: 'GUM', GW: 'GNB', GY: 'GUY', HK: 'HKG', HM: 'HMD', HN: 'HND', HR: 'HRV', HT: 'HTI', HU: 'HUN',
  ID: 'IDN', IE: 'IRL', IL: 'ISR', IM: 'IMN', IN: 'IND', IO: 'IOT', IQ: 'IRQ', IR: 'IRN', IS: 'ISL', IT: 'ITA',
  JE: 'JEY', JM: 'JAM', JO: 'JOR', JP: 'JPN', KE: 'KEN', KG: 'KGZ', KH: 'KHM', KI: 'KIR', KM: 'COM', KN: 'KNA',
  KP: 'PRK', KR: 'KOR', KW: 'KWT', KY: 'CYM', KZ: 'KAZ', LA: 'LAO', LB: 'LBN', LC: 'LCA', LI: 'LIE', LK: 'LKA',
  LR: 'LBR', LS: 'LSO', LT: 'LTU', LU: 'LUX', LV: 'LVA', LY: 'LBY', MA: 'MAR', MC: 'MCO', MD: 'MDA', ME: 'MNE',
  MF: 'MAF', MG: 'MDG', MH: 'MHL', MK: 'MKD', ML: 'MLI', MM: 'MMR', MN: 'MNG', MO: 'MAC', MP: 'MNP', MQ: 'MTQ',
  MR: 'MRT', MS: 'MSR', MT: 'MLT', MU: 'MUS', MV: 'MDV', MW: 'MWI', MX: 'MEX', MY: 'MYS', MZ: 'MOZ', NA: 'NAM',
  NC: 'NCL', NE: 'NER', NF: 'NFK', NG: 'NGA', NI: 'NIC', NL: 'NLD', NO: 'NOR', NP: 'NPL', NR: 'NRU', NU: 'NIU',
  NZ: 'NZL', OM: 'OMN', PA: 'PAN', PE: 'PER', PF: 'PYF', PG: 'PNG', PH: 'PHL', PK: 'PAK', PL: 'POL', PM: 'SPM',
  PN: 'PCN', PR: 'PRI', PS: 'PSE', PT: 'PRT', PW: 'PLW', PY: 'PRY', QA: 'QAT', RE: 'REU', RO: 'ROU', RS: 'SRB',
  RU: 'RUS', RW: 'RWA', SA: 'SAU', SB: 'SLB', SC: 'SYC', SD: 'SDN', SE: 'SWE', SG: 'SGP', SH: 'SHN', SI: 'SVN',
  SJ: 'SJM', SK: 'SVK', SL: 'SLE', SM: 'SMR', SN: 'SEN', SO: 'SOM', SR: 'SUR', SS: 'SSD', ST: 'STP', SV: 'SLV',
  SX: 'SXM', SY: 'SYR', SZ: 'SWZ', TC: 'TCA', TD: 'TCD', TF: 'ATF', TG: 'TGO', TH: 'THA', TJ: 'TJK', TK: 'TKL',
  TL: 'TLS', TM: 'TKM', TN: 'TUN', TO: 'TON', TR: 'TUR', TT: 'TTO', TV: 'TUV', TW: 'TWN', TZ: 'TZA', UA: 'UKR',
  UG: 'UGA', UM: 'UMI', US: 'USA', UY: 'URY', UZ: 'UZB', VA: 'VAT', VC: 'VCT', VE: 'VEN', VG: 'VGB', VI: 'VIR',
  VN: 'VNM', VU: 'VUT', WF: 'WLF', WS: 'WSM', XK: 'XKX', YE: 'YEM', YT: 'MYT', ZA: 'ZAF', ZM: 'ZMB', ZW: 'ZWE',
};

/** Every country the card offers, by alpha-2 code (the table's keys). */
export const COUNTRY_CODES: ReadonlyArray<string> = Object.keys(ALPHA3_BY_ALPHA2);

/**
 * Map shapes whose id is not an alpha-3 code, folded into the country they belong to: `IC` is the Canary Islands
 * (Spain), drawn as its own shape. Its tone follows `ES`.
 */
const SHAPE_OWNER: Readonly<Record<string, string>> = { IC: 'ES' };

const ALPHA2_BY_SHAPE: Readonly<Record<string, string>> = {
  ...Object.fromEntries(Object.entries(ALPHA3_BY_ALPHA2).map(([alpha2, alpha3]) => [alpha3, alpha2])),
  ...SHAPE_OWNER,
};

/**
 * Every alpha-3 code in the table, plus the `IC` shape, with the alpha-2 country it belongs to. 40 codes (small islands
 * and microstates such as `MC`, `SM`, `VA`, `GI`, and `XK` Kosovo) have no shape on the map: they stay in the list and
 * simply cannot be tinted.
 */
export const MAP_SHAPES: ReadonlyArray<readonly [shapeId: string, alpha2: string]> = Object.entries(ALPHA2_BY_SHAPE);

const regionNames = new Map<string, Intl.DisplayNames | null>();

const regionNamesIn = (lang: string): Intl.DisplayNames | null => {
  if (!regionNames.has(lang)) {
    try {
      regionNames.set(lang, new Intl.DisplayNames([lang], { type: 'region' }));
    } catch {
      regionNames.set(lang, null);
    }
  }
  return regionNames.get(lang) ?? null;
};

/** Alpha-2 code → country name in `lang` (a BCP-47 language tag); the bare code when `Intl.DisplayNames` has none. */
export const countryName = (code: string, lang: string): string => {
  const names = regionNamesIn(lang);
  if (!names) return code;
  try {
    return names.of(code) ?? code;
  } catch {
    return code;
  }
};

export type CountryEntry = { code: string; name: string };

const listsByLang = new Map<string, CountryEntry[]>();

/** Every country the card offers, named and sorted in `lang` (a BCP-47 language tag). Built once per language. */
export const listCountries = (lang: string): CountryEntry[] => {
  let list = listsByLang.get(lang);
  if (!list) {
    list = COUNTRY_CODES.map((code) => ({ code, name: countryName(code, lang) })).sort((a, b) =>
      a.name.localeCompare(b.name, lang),
    );
    listsByLang.set(lang, list);
  }
  return list;
};
